/* AdminDashboard — Wave 13: ORIO OS look, new tabs Rifugi & Hazards. */
import React, { useEffect, useState, useCallback } from "react";
import {
  Users, Dog, AlertTriangle, Briefcase, Footprints, CreditCard, Trash2,
  ShieldCheck, ShieldAlert, Home as HomeIcon, MapPin, Check, X, Clock,
  Inbox, Activity, Phone, Mail, Download as DownloadIcon, Smartphone, Apple, Monitor, Wifi, Globe, Camera, Upload, Sparkles as SparklesIcon,
  HeartHandshake, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatErr } from "@/lib/api";
import { Section, List } from "@/components/orio";

const TABS = [
  { k: "overview",    l: "Panoramica",   icon: Activity },
  { k: "online",      l: "Online",       icon: Wifi },
  { k: "install",     l: "Install",      icon: DownloadIcon },
  { k: "images",      l: "Immagini",     icon: Camera },
  { k: "shelter_queue", l: "Coda Rifugi", icon: HeartHandshake },
  { k: "shelters",    l: "Rifugi (old)", icon: HomeIcon },
  { k: "hazards",     l: "Hazards",      icon: AlertTriangle },
  { k: "users",       l: "Utenti",       icon: Users },
  { k: "dogs",        l: "Cani",         icon: Dog },
  { k: "emergencies", l: "Emergenze",    icon: AlertTriangle },
  { k: "missions",    l: "Missioni",     icon: Briefcase },
  { k: "tx",          l: "Pagamenti",    icon: CreditCard },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [tab, setTab] = useState("overview");

  const [users, setUsers] = useState([]);
  const [showFakeUsers, setShowFakeUsers] = useState(false);
  const [dogs, setDogs] = useState([]);
  const [emergencies, setEmergencies] = useState([]);
  const [missions, setMissions] = useState([]);
  const [txs, setTxs] = useState([]);
  const [shelters, setShelters] = useState([]);
  const [shelterQueue, setShelterQueue] = useState([]);
  const [hazards, setHazards] = useState([]);

  const loadAll = useCallback(async () => {
    const [s, u, d, e, m, t, sh, h, fn, sq] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/users", { params: { real_only: !showFakeUsers } }),
      api.get("/admin/dogs"),
      api.get("/admin/emergencies"),
      api.get("/admin/missions"),
      api.get("/admin/transactions"),
      api.get("/admin/shelters"),
      api.get("/admin/hazards"),
      api.get("/admin/funnel"),
      api.get("/admin/shelter/queue"),
    ]);
    setStats(s.data); setFunnel(fn.data);
    setUsers(u.data); setDogs(d.data); setEmergencies(e.data); setMissions(m.data);
    setTxs(t.data);
    setShelters(sh.data); setHazards(h.data);
    setShelterQueue(sq.data.items || []);
  }, [showFakeUsers]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const delUser = async (id) => {
    if (!confirm("Eliminare utente e tutti i suoi dati?")) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("Utente eliminato");
    loadAll();
  };

  const giftPro = async (id) => {
    const daysStr = prompt("Quanti giorni di ORIO Pro regalare?", "30");
    if (!daysStr) return;
    const days = parseInt(daysStr, 10);
    if (isNaN(days) || days < 1) return toast.error("Numero giorni non valido");
    const reason = prompt("Motivazione (opzionale):", "") || null;
    try {
      const { data } = await api.post(`/admin/users/${id}/gift-pro`, { days, reason });
      toast.success(`Pro attivato fino al ${new Date(data.pro_until).toLocaleDateString("it-IT")}`);
      loadAll();
    } catch { toast.error("Errore gift Pro"); }
  };

  const revokeSub = async (id) => {
    if (!confirm("Revocare ORIO Pro subito? L'utente perderà i benefici immediatamente.")) return;
    try {
      await api.post(`/admin/users/${id}/revoke-subscription`);
      toast.success("Abbonamento revocato");
      loadAll();
    } catch { toast.error("Errore revoca"); }
  };

  const delMission = async (id) => {
    if (!confirm("Cancellare questa missione?")) return;
    try {
      await api.delete(`/admin/missions/${id}`);
      toast.success("Missione cancellata");
      loadAll();
    } catch { toast.error("Errore"); }
  };

  const verifyShelterV2 = async (sid, status, reason) => {
    try {
      await api.post(`/admin/shelter/${sid}/verify`, { status, rejection_reason: reason || null });
      toast.success(status === "verified" ? "Rifugio verificato" : "Rifugio rifiutato");
      loadAll();
    } catch { toast.error("Errore"); }
  };

  const deleteShelterV2 = async (sid) => {
    if (!confirm("Eliminare definitivamente il rifugio?")) return;
    try {
      await api.delete(`/admin/shelter/${sid}`);
      toast.success("Rifugio eliminato");
      loadAll();
    } catch { toast.error("Errore"); }
  };

  const verifyShelter = async (sid, verified) => {
    try {
      await api.post(`/admin/shelters/${sid}/verify`, { verified });
      toast.success(verified ? "Rifugio verificato" : "Verifica revocata");
      loadAll();
    } catch {
      toast.error("Errore durante la verifica");
    }
  };

  const approveHazard = async (hid) => {
    try {
      await api.post(`/admin/hazards/${hid}/approve`);
      toast.success("Hazard approvato");
      loadAll();
    } catch {
      toast.error("Errore approvazione");
    }
  };

  const deleteHazard = async (hid) => {
    if (!confirm("Rimuovere questo hazard dalla mappa?")) return;
    try {
      await api.delete(`/admin/hazards/${hid}`);
      toast.success("Hazard rimosso");
      loadAll();
    } catch {
      toast.error("Errore rimozione");
    }
  };

  if (!stats) {
    return <div className="max-w-6xl mx-auto p-10 text-[#9A9A9A] text-sm">Caricamento console…</div>;
  }

  return (
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-32">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFB020] inline-flex items-center gap-1.5 mb-2">
        <ShieldAlert className="h-3 w-3" /> Area Admin
      </div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
        Console <span className="orio-grad-text">operativa ORIO</span>
      </h1>
      <p className="text-[#9A9A9A] text-sm mt-1.5 max-w-2xl">
        Verifica rifugi, modera hazard sulla mappa, gestisci utenti, abbonamenti e missioni.
      </p>

      {/* Tabs strip */}
      <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.k;
          const badge = t.k === "shelters" ? stats.shelters_pending
            : t.k === "hazards" ? stats.hazards_pending
            : 0;
          return (
            <button
              key={t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border inline-flex items-center gap-1.5 ${
                active ? "bg-[#FF7A1A] text-white border-[#FF7A1A] shadow-[0_4px_12px_-4px_rgba(255,122,26,0.4)]" : "bg-white text-[#6B6B6B] hover:text-[#1A1A1A] border-[#F0EBE3]"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.l}
              {badge > 0 && (
                <span className="ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                  style={{ background: active ? "rgba(0,0,0,0.15)" : "#FF3B30", color: active ? "#000" : "#fff" }}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* CONTENT */}
      {tab === "overview" && <Overview stats={stats} funnel={funnel} />}

      {tab === "online" && <OnlinePanel />}

      {tab === "install" && <InstallAnalyticsPanel />}

      {tab === "images" && <IllustrationsPanel />}

      {tab === "shelter_queue" && (
        <Section
          eyebrow="Coda verifiche"
          title={`${shelterQueue.filter(s => s.status === 'pending').length} in attesa · ${shelterQueue.length} totali`}
          action={<span className="text-[11px] text-[#9A9A9A]">Rifugi v2 self-service</span>}
        >
          {shelterQueue.length === 0 ? (
            <Empty label="Nessuna richiesta rifugio da valutare." />
          ) : (
            <div className="space-y-3">
              {shelterQueue.map((s) => (
                <ShelterV2Card
                  key={s.id}
                  s={s}
                  onVerify={(status, reason) => verifyShelterV2(s.id, status, reason)}
                  onDelete={() => deleteShelterV2(s.id)}
                />
              ))}
            </div>
          )}
        </Section>
      )}

      {tab === "shelters" && (
        <Section eyebrow="Verifica rifugi" title="Rifugi e associazioni registrate"
          action={<span className="text-[11px] text-[#9A9A9A]">{shelters.length} totali · {stats.shelters_verified} verificati</span>}>
          {shelters.length === 0 ? (
            <Empty label="Nessun rifugio registrato." />
          ) : (
            <div className="space-y-3">
              {shelters.map((s) => (
                <ShelterCard key={s.id} s={s} onVerify={(v) => verifyShelter(s.id, v)} />
              ))}
            </div>
          )}
        </Section>
      )}

      {tab === "hazards" && (
        <Section eyebrow="Moderazione mappa" title="Hazard segnalati"
          action={<span className="text-[11px] text-[#9A9A9A]">{hazards.length} totali · {stats.hazards_pending} da moderare</span>}>
          {hazards.length === 0 ? (
            <Empty label="Nessun hazard segnalato." />
          ) : (
            <div className="space-y-2">
              {hazards.map((h) => (
                <HazardRow key={h.id} h={h} onApprove={() => approveHazard(h.id)} onDelete={() => deleteHazard(h.id)} />
              ))}
            </div>
          )}
        </Section>
      )}

      {tab === "users" && (
        <Section
          eyebrow="Utenti"
          title={`${users.length} ${showFakeUsers ? "totali" : "reali"} · ordinati per registrazione`}
          action={
            <label className="inline-flex items-center gap-2 text-[11px] text-[#6B6B6B] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showFakeUsers}
                onChange={(e) => setShowFakeUsers(e.target.checked)}
                data-testid="admin-users-show-fake"
                className="h-3.5 w-3.5"
              />
              Mostra anche account fake / seed
            </label>
          }
        >
          <Table cols={["Nome", "Email", "Ruolo", "Prova", "Abbonamento", "Registrato", "Ultima visita", ""]}>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#F0EBE3]">
                <Td>{u.name}</Td>
                <Td className="text-[#6B6B6B]">{u.email}</Td>
                <Td>
                  {u.role === "admin"
                    ? <Pill color="#FFB020">Admin</Pill>
                    : u.is_shelter
                      ? <Pill color="#FF3B30">Rifugio</Pill>
                      : <Pill color="#9aa0a6">Utente</Pill>}
                </Td>
                <Td>{u.trial_ends_at ? new Date(u.trial_ends_at).toLocaleDateString("it-IT") : "—"}</Td>
                <Td>{u.subscription_ends_at ? new Date(u.subscription_ends_at).toLocaleDateString("it-IT") : "—"}</Td>
                <Td>{u.created_at ? new Date(u.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</Td>
                <Td className="text-[#6B6B6B]">
                  {u.last_ping_at ? <RelativeTime iso={u.last_ping_at} /> : <span className="text-[#C4B79A]">mai</span>}
                </Td>
                <Td>
                  <div className="flex items-center gap-1.5 flex-wrap justify-end">
                    <button
                      data-testid={`gift-pro-${u.id}`}
                      onClick={() => giftPro(u.id)}
                      className="px-2 py-0.5 rounded-full text-[10.5px] font-semibold text-white bg-[#2DBA60] hover:opacity-90"
                      title="Regala ORIO Pro"
                    >
                      🎁 Pro
                    </button>
                    {u.is_pro && (
                      <button
                        data-testid={`revoke-sub-${u.id}`}
                        onClick={() => revokeSub(u.id)}
                        className="px-2 py-0.5 rounded-full text-[10.5px] font-semibold text-white bg-[#F59E0B] hover:opacity-90"
                        title="Revoca abbonamento"
                      >
                        Revoca
                      </button>
                    )}
                    {u.role !== "admin" && (
                      <button data-testid={`del-user-${u.id}`} onClick={() => delUser(u.id)}
                        className="text-[#FF3B30] hover:opacity-80" title="Elimina utente">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      {tab === "dogs" && (
        <Section eyebrow="Cani" title={`${dogs.length} passaporti`}>
          <Table cols={["Nome", "Razza", "Proprietario", "Stub", "Creato"]}>
            {dogs.map((d) => (
              <tr key={d.id} className="border-t border-[#F0EBE3]">
                <Td>{d.name}</Td>
                <Td className="text-[#6B6B6B]">{d.breed || "—"}</Td>
                <Td className="text-[#6B6B6B]">{d.owner_email || d.owner_id}</Td>
                <Td>{d.is_stub ? <Pill color="#FFB020">Sì</Pill> : <Pill color="#34D399">No</Pill>}</Td>
                <Td>{d.created_at ? new Date(d.created_at).toLocaleDateString("it-IT") : "—"}</Td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      {tab === "emergencies" && (
        <Section eyebrow="Emergenze" title={`${emergencies.length} segnalazioni`}>
          <Table cols={["Cane", "Proprietario", "Posizione", "Stato", "Creato"]}>
            {emergencies.map((e) => (
              <tr key={e.id} className="border-t border-[#F0EBE3]">
                <Td>{e.dog_name}</Td>
                <Td className="text-[#6B6B6B]">{e.owner_name}</Td>
                <Td className="font-mono text-[11px] text-[#9A9A9A]">{e.lat.toFixed(3)}, {e.lng.toFixed(3)}</Td>
                <Td>{e.active ? <Pill color="#FF3B30">Attiva</Pill> : <Pill color="#9aa0a6">Chiusa</Pill>}</Td>
                <Td>{new Date(e.created_at).toLocaleString("it-IT")}</Td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      {tab === "missions" && (
        <Section eyebrow="Missioni & Match" title={`${missions.length} record`}>
          <Table cols={["Tipo", "Titolo", "Categoria", "Owner", "Attiva", "Creato", ""]}>
            {missions.map((m) => (
              <tr key={m.id} className="border-t border-[#F0EBE3]">
                <Td>{m.kind}</Td>
                <Td>{m.title}</Td>
                <Td className="text-[#6B6B6B]">{m.category || "—"}</Td>
                <Td className="text-[#6B6B6B]">{m.owner_email || m.owner_id}</Td>
                <Td>{m.active ? <Pill color="#34D399">Sì</Pill> : <Pill color="#9aa0a6">No</Pill>}</Td>
                <Td>{new Date(m.created_at).toLocaleDateString("it-IT")}</Td>
                <Td>
                  <button data-testid={`del-mission-${m.id}`} onClick={() => delMission(m.id)}
                    className="text-[#FF3B30] hover:opacity-80" title="Cancella missione">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </Td>
              </tr>
            ))}
          </Table>
        </Section>
      )}

      {tab === "tx" && (
        <Section eyebrow="Pagamenti Stripe" title={`${txs.length} transazioni`}>
          <Table cols={["Utente", "Importo", "Stato", "Session ID", "Creato"]}>
            {txs.map((t) => (
              <tr key={t.id} className="border-t border-[#F0EBE3]">
                <Td className="text-[#6B6B6B]">{t.user_email}</Td>
                <Td className="font-semibold">{t.amount} {t.currency?.toUpperCase()}</Td>
                <Td>
                  <Pill color={t.payment_status === "paid" ? "#34D399" : "#FFB020"}>
                    {t.payment_status}
                  </Pill>
                </Td>
                <Td className="font-mono text-[11px] text-[#9A9A9A]">{t.session_id?.slice(0, 24)}…</Td>
                <Td>{new Date(t.created_at).toLocaleString("it-IT")}</Td>
              </tr>
            ))}
          </Table>
        </Section>
      )}
    </div>
  );
}

/* ====================================================== */
/* Overview                                               */
/* ====================================================== */
function Overview({ stats, funnel }) {
  return (
    <>
      <Section eyebrow="Operativo" title="Cosa serve attenzione">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <ActionStat
            icon={HomeIcon}
            color="#FFB020"
            label="Rifugi da verificare"
            value={stats.shelters_pending}
            sub={`${stats.shelters_verified} già verificati`}
            testid="ov-shelters"
          />
          <ActionStat
            icon={AlertTriangle}
            color="#FF3B30"
            label="Hazard da moderare"
            value={stats.hazards_pending}
            sub={`${stats.hazards_active} attivi sulla mappa`}
            testid="ov-hazards"
          />
          <ActionStat
            icon={CreditCard}
            color="#34D399"
            label="Abbonati attivi"
            value={stats.subscribed}
            sub={`su ${stats.users} utenti`}
            testid="ov-subscribed"
          />
          <ActionStat
            icon={AlertTriangle}
            color="#FF3B30"
            label="Emergenze aperte"
            value={stats.emergencies_active}
            sub={`${stats.emergencies_total} storiche`}
            testid="ov-emergencies"
          />
        </div>
      </Section>

      {funnel && (
        <Section eyebrow="Conversione landing → app" title="Funnel installazione (ultimi 30 giorni)">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <ActionStat
              icon={Activity}
              color="#5B9EE0"
              label="Visitatori (7gg)"
              value={funnel.visitors_7d}
              sub={`${funnel.shown_30d} banner mostrati in 30gg`}
              testid="fn-visitors"
            />
            <ActionStat
              icon={SparklesIcon}
              color="#FFB020"
              label="CTA · aperto Registrati"
              value={funnel.register_opened_30d}
              sub={`${funnel.registrations_30d} registrazioni completate`}
              testid="fn-cta"
            />
            <ActionStat
              icon={DownloadIcon}
              color="#2DBA60"
              label="Installazioni PWA · 30gg"
              value={funnel.installed_30d}
              sub={`${funnel.installed_all} totali all-time`}
              testid="fn-installed"
            />
            <ActionStat
              icon={Clock}
              color="#8B5CF6"
              label="Permanenza media"
              value={fmtDuration(funnel.avg_visit_seconds)}
              sub={`CTA→install ${funnel.cta_to_install_rate}% · CTA→reg ${funnel.register_conversion}%`}
              testid="fn-avg-visit"
            />
          </div>
        </Section>
      )}

      <Section eyebrow="Metriche piattaforma" title="Numeri ORIO">
        <List>
          <MetricRow icon={Users} label="Utenti totali" value={stats.users} color="#34D399" />
          <MetricRow icon={Dog} label="Passaporti cane" value={stats.dogs} color="#fff" />
          <MetricRow icon={HomeIcon} label="Rifugi verificati / totali" value={`${stats.shelters_verified} / ${stats.shelters_total}`} color="#FFB020" />
          <MetricRow icon={Briefcase} label="Missioni attive" value={stats.missions_active} color="#FFB020" />
          <MetricRow icon={Footprints} label="Match attivi" value={stats.matches_active} color="#34D399" />
          <MetricRow icon={AlertTriangle} label="Hazard attivi sulla mappa" value={stats.hazards_active} color="#FF3B30" />
        </List>
      </Section>
    </>
  );
}

/* Format seconds as "5 min", "1h 20m", "2d 3h". Used by the funnel widget. */
function fmtDuration(seconds) {
  if (!seconds || seconds < 60) return `${seconds || 0}s`;
  const min = Math.floor(seconds / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const rem = min % 60;
  if (h < 24) return rem ? `${h}h ${rem}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH ? `${d}g ${remH}h` : `${d}g`;
}

/* Human-friendly "3 minutes ago" style timestamp for the Users table. */
function RelativeTime({ iso }) {
  if (!iso) return <span className="text-[#C4B79A]">—</span>;
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.floor((now - then) / 1000);
  let label;
  if (s < 60) label = "adesso";
  else if (s < 3600) label = `${Math.floor(s / 60)} min fa`;
  else if (s < 86400) label = `${Math.floor(s / 3600)} h fa`;
  else if (s < 86400 * 30) label = `${Math.floor(s / 86400)} g fa`;
  else label = new Date(iso).toLocaleDateString("it-IT");
  const fresh = s < 3600 * 24; // green if within 24h
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: fresh ? "#2DBA60" : "#C4B79A" }}
      />
      <span className={fresh ? "text-[#1A1A1A]" : ""}>{label}</span>
    </span>
  );
}

function ActionStat({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[#F0EBE3] p-4 bg-white"
      style={{
        background: `linear-gradient(160deg, ${color}12 0%, #FFFFFF 65%)`,
        boxShadow: "0 6px 16px -8px rgba(0,0,0,0.06)",
      }}
    >
      <div className="flex items-center justify-between">
        <Icon className="h-4 w-4" style={{ color }} />
        <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color }}>{label}</span>
      </div>
      <div className="font-display text-3xl tabular-nums mt-2 leading-none text-[#1A1A1A]">{value}</div>
      {sub && <div className="text-[11px] text-[#7A7A7A] mt-1.5">{sub}</div>}
    </div>
  );
}

function MetricRow({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center gap-3 px-4 sm:px-5 py-3.5">
      <div
        className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
        style={{ background: `${color}1F`, color, border: `1px solid ${color}33` }}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 text-[14px] text-[#1A1A1A]">{label}</div>
      <div className="font-display text-xl tabular-nums">{value}</div>
    </div>
  );
}

/* ====================================================== */
/* ShelterCard                                            */
/* ====================================================== */
function ShelterCard({ s, onVerify }) {
  return (
    <div
      data-testid={`shelter-row-${s.id}`}
      className="rounded-2xl border border-[#F0EBE3] p-4 sm:p-5 bg-white"
      style={{
        boxShadow: "0 6px 16px -8px rgba(0,0,0,0.06)",
      }}
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0"
          style={{
            background: s.verified ? "rgba(52,211,153,0.20)" : "rgba(255,176,32,0.18)",
            color: s.verified ? "#34D399" : "#FFB020",
            border: `1px solid ${s.verified ? "rgba(52,211,153,0.45)" : "rgba(255,176,32,0.40)"}`,
          }}>
          <HomeIcon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg leading-tight text-[#1A1A1A]">{s.name || "—"}</div>
            {s.verified ? (
              <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold inline-flex items-center gap-1"
                style={{ background: "rgba(52,211,153,0.18)", color: "#34D399", border: "1px solid rgba(52,211,153,0.40)" }}>
                <ShieldCheck className="h-3 w-3" /> Verificato
              </span>
            ) : (
              <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold"
                style={{ background: "rgba(255,176,32,0.15)", color: "#FFB020", border: "1px solid rgba(255,176,32,0.35)" }}>
                Da verificare
              </span>
            )}
            {s.is_subscribed && <Pill color="#34D399">Pro</Pill>}
          </div>
          <div className="text-[11px] text-[#9A9A9A] mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap">
            <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {s.email}</span>
            {s.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {s.phone}</span>}
            {s.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {s.city}</span>}
            {s.created_at && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(s.created_at).toLocaleDateString("it-IT")}</span>}
          </div>
          {s.bio && <p className="text-[12px] text-[#9A9A9A] mt-2 line-clamp-2 leading-relaxed">{s.bio}</p>}
          <div className="mt-2 flex items-center gap-3 text-[10px] uppercase tracking-widest">
            <span><span className="text-[#FFB020] font-semibold">{s.adoptable_count}</span> <span className="text-[#9A9A9A]">adottabili</span></span>
            <span><span className="text-[#FFB020] font-semibold">{s.open_missions}</span> <span className="text-[#9A9A9A]">missioni</span></span>
            <span><span className="text-[#FF3B30] font-semibold">{s.pending_candidacies}</span> <span className="text-[#9A9A9A]">candidature</span></span>
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {s.verified ? (
            <button
              data-testid={`shelter-unverify-${s.id}`}
              onClick={() => onVerify(false)}
              className="px-3.5 py-2 rounded-full bg-white hover:bg-[#F4EFE6] text-xs inline-flex items-center gap-1.5 border border-[#F0EBE3]"
            >
              <X className="h-3.5 w-3.5" /> Revoca
            </button>
          ) : (
            <button
              data-testid={`shelter-verify-${s.id}`}
              onClick={() => onVerify(true)}
              className="px-3.5 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
              style={{ background: "rgba(52,211,153,0.18)", color: "#34D399", border: "1px solid rgba(52,211,153,0.45)" }}
            >
              <ShieldCheck className="h-3.5 w-3.5" /> Verifica
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ====================================================== */
/* HazardRow                                              */
/* ====================================================== */
const HAZARD_TYPE_LABEL = {
  ice: "Ghiaccio / asfalto",
  glass: "Vetri / detriti",
  bait: "Esca avvelenata",
  trap: "Trappola",
  dog_pack: "Branco aggressivo",
  road: "Strada pericolosa",
  other: "Altro",
};

function ShelterV2Card({ s, onVerify, onDelete }) {
  const isPending = s.status === "pending";
  const isVerified = s.status === "verified";
  const isRejected = s.status === "rejected";
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState("");

  return (
    <div className="rounded-2xl bg-white border border-[#F0EBE3] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg text-[#1A1A1A]">{s.name}</div>
            {isPending && <Pill color="#F59E0B">Pending</Pill>}
            {isVerified && <Pill color="#2DBA60">Verified</Pill>}
            {isRejected && <Pill color="#FF3B30">Rifiutato</Pill>}
          </div>
          <div className="mt-1 text-[12px] text-[#7A7A7A] inline-flex items-center gap-1.5">
            <MapPin className="h-3 w-3" /> {s.city}{s.address ? ` · ${s.address}` : ""}
          </div>
          {s.tax_code && <div className="text-[11px] text-[#9A9A9A] mt-0.5">CF/P.IVA: {s.tax_code}</div>}
          <div className="mt-2 text-[12.5px] text-[#4A4A4A] line-clamp-3">{s.description}</div>
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-[#7A7A7A]">
            {s.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {s.email}</span>}
            {s.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {s.phone}</span>}
            {s.website && <span className="inline-flex items-center gap-1"><Globe className="h-3 w-3" /> {s.website}</span>}
          </div>
          <div className="mt-1 text-[10.5px] text-[#B0A084]">
            Richiesto da <strong>{s.owner_name || s.owner_email}</strong>
            {s.submitted_at ? ` · ${new Date(s.submitted_at).toLocaleString("it-IT")}` : ""}
          </div>
          {isRejected && s.rejection_reason && (
            <div className="mt-2 text-[12px] text-[#C25612] bg-[#FFEEDD] rounded-lg px-2 py-1">
              Motivo rifiuto: {s.rejection_reason}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          {isPending && (
            <>
              <button
                data-testid={`shelter-v2-verify-${s.id}`}
                onClick={() => onVerify("verified")}
                className="px-3 h-9 rounded-full bg-[#2DBA60] text-white text-[12px] font-semibold inline-flex items-center gap-1"
              ><Check className="h-3.5 w-3.5" /> Verifica</button>
              <button
                onClick={() => setShowReject((v) => !v)}
                className="px-3 h-9 rounded-full bg-[#FFEEDD] text-[#C25612] text-[12px] font-semibold inline-flex items-center gap-1"
              ><X className="h-3.5 w-3.5" /> Rifiuta</button>
            </>
          )}
          <button
            data-testid={`shelter-v2-del-${s.id}`}
            onClick={onDelete}
            className="px-3 h-9 rounded-full text-[#FF3B30] text-[12px] font-semibold inline-flex items-center gap-1"
            title="Elimina definitivamente"
          ><Trash2 className="h-3.5 w-3.5" /> Elimina</button>
        </div>
      </div>
      {showReject && (
        <div className="mt-3 p-3 rounded-xl bg-[#FBF7EF] border border-[#F0EBE3]">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motivo del rifiuto (es. mancano documenti P.IVA)"
            className="w-full rounded-lg px-3 py-2 text-[13px] bg-white border border-[#F0EBE3] outline-none focus:border-[#FF7A1A]"
          />
          <div className="mt-2 flex gap-2 justify-end">
            <button onClick={() => setShowReject(false)}
              className="px-3 h-9 rounded-full bg-white border border-[#F0EBE3] text-[12px] font-semibold text-[#7A7A7A]">
              Annulla
            </button>
            <button
              onClick={() => { onVerify("rejected", reason); setShowReject(false); }}
              className="px-3 h-9 rounded-full bg-[#FF3B30] text-white text-[12px] font-semibold"
            >Conferma rifiuto</button>
          </div>
        </div>
      )}
    </div>
  );
}

function HazardRow({ h, onApprove, onDelete }) {
  const active = h.active !== false;
  const moderated = h.moderated === true;
  const status = h.moderation_status;
  return (
    <div
      data-testid={`hazard-row-${h.id}`}
      className="rounded-2xl border border-[#F0EBE3] p-3.5 sm:p-4 flex items-start gap-3"
      style={{ background: active ? "rgba(255,59,48,0.04)" : "#FAF7F0" }}
    >
      <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
        style={{
          background: active ? "rgba(255,59,48,0.18)" : "rgba(154,160,166,0.18)",
          color: active ? "#FF3B30" : "#9aa0a6",
          border: `1px solid ${active ? "rgba(255,59,48,0.40)" : "rgba(154,160,166,0.30)"}`,
        }}>
        <AlertTriangle className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#FF3B30] font-semibold">
            {HAZARD_TYPE_LABEL[h.type] || h.type || "Hazard"}
          </span>
          {!active && <Pill color="#9aa0a6">Rimosso</Pill>}
          {moderated && status === "approved" && <Pill color="#34D399">Approvato</Pill>}
          <span className="text-[#C7C0B3] text-[10px]">·</span>
          <span className="text-[#9A9A9A] text-[10px] inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {h.created_at ? new Date(h.created_at).toLocaleString("it-IT") : "—"}
          </span>
        </div>
        <div className="text-[14px] mt-0.5 line-clamp-2">{h.note || h.description || "(nessuna nota)"}</div>
        <div className="text-[11px] text-[#9A9A9A] mt-1.5 flex items-center gap-x-3 gap-y-1 flex-wrap">
          <span className="inline-flex items-center gap-1 font-mono">
            <MapPin className="h-3 w-3" /> {h.lat?.toFixed(4)}, {h.lng?.toFixed(4)}
          </span>
          <span className="inline-flex items-center gap-1">
            <Inbox className="h-3 w-3" /> {h.reporter_email || h.reporter_name || "Anonimo"}
          </span>
          {(h.confirms || []).length > 0 && (
            <span className="text-[#34D399]">{h.confirms.length} conferme</span>
          )}
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-2">
        {active && !moderated && (
          <button
            data-testid={`hazard-approve-${h.id}`}
            onClick={onApprove}
            className="px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"
            style={{ background: "rgba(52,211,153,0.18)", color: "#34D399", border: "1px solid rgba(52,211,153,0.40)" }}
          >
            <Check className="h-3.5 w-3.5" /> Approva
          </button>
        )}
        {active && (
          <button
            data-testid={`hazard-delete-${h.id}`}
            onClick={onDelete}
            className="px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 border"
            style={{ background: "rgba(255,59,48,0.10)", color: "#FF3B30", borderColor: "rgba(255,59,48,0.40)" }}
          >
            <Trash2 className="h-3.5 w-3.5" /> Rimuovi
          </button>
        )}
      </div>
    </div>
  );
}

/* ====================================================== */
/* Small utilities                                        */
/* ====================================================== */
function Pill({ color, children }) {
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold"
      style={{ background: `${color}1F`, color, border: `1px solid ${color}33` }}>
      {children}
    </span>
  );
}

function Empty({ label }) {
  return (
    <div className="rounded-2xl border border-[#F0EBE3] p-10 text-center"
      style={{ background: "#FAF7F0" }}>
      <div className="text-[14px] text-[#6B6B6B]">{label}</div>
    </div>
  );
}

function Table({ cols, children }) {
  return (
    <div className="rounded-2xl border border-[#F0EBE3] overflow-hidden overflow-x-auto"
      style={{
        background: "linear-gradient(180deg, #FFFFFF 0%, #FBF7F1 100%)",
        backdropFilter: "blur(20px) saturate(150%)",
      }}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-widest text-[#9A9A9A]">
            {cols.map((c, i) => <th key={i} className="px-4 sm:px-5 py-3 font-medium">{c}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Td({ children, className = "", colSpan }) {
  return <td colSpan={colSpan} className={`px-4 sm:px-5 py-3 ${className}`}>{children}</td>;
}

/* ============================================================================
   Install Analytics Panel — funnel for the SmartInstallBanner
   ============================================================================ */
function InstallAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/install-analytics");
        setData(r.data);
      } catch (e) {
        toast.error("Impossibile caricare le statistiche");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="text-sm text-[#9A9A9A] px-1">Caricamento metriche...</div>;
  }
  if (!data) return <Empty label="Nessun dato disponibile." />;

  const { funnel, by_platform, totals, daily } = data;
  const platforms = [
    { k: "android", l: "Android", icon: Smartphone, color: "#34D399" },
    { k: "ios",     l: "iOS",     icon: Apple,      color: "#FFB020" },
    { k: "desktop", l: "Desktop", icon: Monitor,    color: "#6EE7F9" },
  ];

  return (
    <div className="space-y-8">
      {/* Funnel summary */}
      <Section eyebrow="Imbuto installazione" title="Da quando lo vedono a quando lo installano"
        action={<span className="text-[11px] text-[#9A9A9A]">Aggregato di tutti i tempi</span>}>
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          <FunnelCard
            label="Banner mostrato"
            value={funnel.shown}
            sub="Utenti che hanno visto la proposta"
            color="#6EE7F9"
          />
          <FunnelCard
            label="Tap su CTA"
            value={funnel.cta_clicked}
            sub={funnel.shown > 0 ? `${funnel.cta_rate}% di chi lo vede` : "—"}
            color="#FFB020"
          />
          <FunnelCard
            label="Installati"
            value={funnel.installed}
            sub={funnel.shown > 0 ? `${funnel.install_rate}% conversion` : "—"}
            color="#34D399"
            highlight
          />
        </div>
        <div className="mt-5 text-[11px] text-[#9A9A9A] leading-relaxed">
          ℹ️ Su iOS Safari Apple non espone l'evento <code className="text-[#6B6B6B]">appinstalled</code>:
          per chi installa da iPhone l'installazione si registra solo se l'utente riapre poi l'app dalla Home
          (display-mode standalone). I numeri Android sono invece accurati al 100%.
        </div>
      </Section>

      {/* Per-platform breakdown */}
      <Section eyebrow="Per piattaforma" title="Chi vede e chi installa">
        <div className="grid sm:grid-cols-3 gap-3 sm:gap-4">
          {platforms.map((p) => {
            const stats = by_platform?.[p.k] || {};
            const shown = stats.shown || 0;
            const cta = stats.cta_clicked || 0;
            const installed = stats.installed || 0;
            return (
              <div key={p.k} className="rounded-3xl p-5 border border-[#F0EBE3] orio-card">
                <div className="flex items-center gap-2.5 text-sm text-[#1A1A1A] font-medium mb-3">
                  <p.icon className="h-4 w-4" style={{ color: p.color }} />
                  {p.l}
                </div>
                <div className="space-y-2.5">
                  <StatRow label="Visto" value={shown} color="#6EE7F9" />
                  <StatRow label="CTA" value={cta} color="#FFB020" />
                  <StatRow label="Installato" value={installed} color="#34D399" />
                </div>
                <div className="mt-4 pt-3 border-t border-white/5 text-[11px] text-[#9A9A9A]">
                  {shown > 0 ? `Conv: ${((installed / shown) * 100).toFixed(1)}%` : "Nessun dato"}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Other event totals */}
      <Section eyebrow="Eventi raw" title="Conteggi totali">
        <List>
          <KVRow label="Banner mostrato"     value={totals.shown || 0} />
          <KVRow label="CTA cliccato"        value={totals.cta_clicked || 0} />
          <KVRow label="Modale guida aperta" value={totals.modal_opened || 0} />
          <KVRow label="Banner chiuso (X)"   value={totals.dismissed || 0} />
          <KVRow label="Installazioni"       value={totals.installed || 0} highlight />
        </List>
      </Section>

      {/* Daily timeline */}
      {daily && daily.length > 0 && (
        <Section eyebrow="Ultimi 30 giorni" title="Giorno per giorno">
          <div className="overflow-x-auto rounded-3xl border border-[#F0EBE3]"
            style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FBF7F1 100%)" }}>
            <table className="w-full text-sm min-w-[480px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#9A9A9A]">
                  <th className="px-4 py-3 font-medium">Data</th>
                  <th className="px-4 py-3 font-medium">Mostrato</th>
                  <th className="px-4 py-3 font-medium">CTA</th>
                  <th className="px-4 py-3 font-medium">Installato</th>
                  <th className="px-4 py-3 font-medium">Chiuso</th>
                </tr>
              </thead>
              <tbody>
                {daily.slice(-30).reverse().map((d) => (
                  <tr key={d.date} className="border-t border-white/5">
                    <Td className="font-mono text-[11px] text-[#6B6B6B]">{d.date}</Td>
                    <Td>{d.shown || 0}</Td>
                    <Td>{d.cta_clicked || 0}</Td>
                    <Td className="text-emerald-300">{d.installed || 0}</Td>
                    <Td className="text-[#9A9A9A]">{d.dismissed || 0}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}

function FunnelCard({ label, value, sub, color, highlight }) {
  return (
    <div
      className="rounded-3xl p-4 sm:p-5 border relative overflow-hidden"
      style={{
        background: highlight
          ? `linear-gradient(180deg, rgba(52,211,153,0.10) 0%, #FFFFFF 80%)`
          : "linear-gradient(180deg, #FFFFFF 0%, #FBF7F1 100%)",
        borderColor: highlight ? "rgba(52,211,153,0.35)" : "#F4EFE6",
      }}
    >
      <div className="text-[10px] uppercase tracking-widest" style={{ color }}>{label}</div>
      <div className="mt-1 text-3xl sm:text-4xl font-display tracking-tight text-[#1A1A1A]">
        {Number(value).toLocaleString("it-IT")}
      </div>
      <div className="mt-1 text-[11px] text-[#9A9A9A] leading-snug">{sub}</div>
    </div>
  );
}

function StatRow({ label, value, color }) {
  return (
    <div className="flex items-center justify-between text-[13px]">
      <span className="text-[#6B6B6B] inline-flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
        {label}
      </span>
      <span className="font-mono text-[#1A1A1A] tabular-nums">{Number(value).toLocaleString("it-IT")}</span>
    </div>
  );
}

function KVRow({ label, value, highlight }) {
  return (
    <div className={`flex items-center justify-between px-4 sm:px-5 py-3 ${highlight ? "bg-emerald-500/[0.04]" : ""}`}>
      <span className={`text-sm ${highlight ? "text-[#1A1A1A] font-medium" : "text-[#1A1A1A]"}`}>{label}</span>
      <span className={`font-mono tabular-nums text-sm ${highlight ? "text-emerald-300" : "text-[#1A1A1A]"}`}>
        {Number(value).toLocaleString("it-IT")}
      </span>
    </div>
  );
}


/* ==============================================================
   OnlinePanel — live "who is on ORIO right now" (auth + guests).
   Polls /api/admin/presence every 20s.
   ============================================================== */
function OnlinePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/admin/presence");
        if (alive) setData(r.data);
      } catch {/* silent */}
      finally { if (alive) setLoading(false); }
    };
    load();
    const t = setInterval(load, 20_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (loading) return <div className="text-sm text-[#9A9A9A]">Caricamento...</div>;
  if (!data) return <div className="text-sm text-[#9A9A9A]">Nessun dato disponibile</div>;

  const b = data.breakdown || {};
  const items = data.items || [];

  return (
    <>
      <Section eyebrow={`Ultimi ${data.window_min || 5} minuti`} title={`${b.total || 0} persone online adesso`}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ActionStat icon={Users} color="#34D399" label="Autenticati" value={b.auth || 0} testid="on-auth" />
          <ActionStat icon={Globe} color="#7B61FF" label="Guest" value={b.guest || 0} testid="on-guest"
            sub="Visitatori non loggati" />
          <ActionStat icon={Smartphone} color="#FF7A1A" label="PWA installata" value={b.standalone || 0} testid="on-standalone"
            sub={b.standalone ? "in modalità app" : "nessuno con app aperta"} />
          <ActionStat icon={Monitor} color="#5B9EE0" label="Da desktop" value={b.platforms?.desktop || 0} testid="on-desktop"
            sub={`${b.platforms?.mobile || 0} da mobile`} />
        </div>
      </Section>

      <Section eyebrow="Ripartizione" title="Dove e come sono online">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <BreakdownCard title="Sistema operativo" items={b.os || {}} color="#34D399" testid="brk-os" />
          <BreakdownCard title="Pagina attiva" items={b.pages || {}} color="#FF7A1A" testid="brk-page"
            labelMap={{ landing: "Landing / sito", app: "App (home)", "app/mappa": "App · Mappa", "app/missioni": "App · Missioni", "app/profilo": "App · Profilo", "app/diario": "App · Diario", "app/match": "App · Match", "app/mio-cane": "App · I miei cani", register: "Registrazione", login: "Login" }} />
          <BreakdownCard title="Dispositivo" items={b.platforms || {}} color="#7B61FF" testid="brk-platform"
            labelMap={{ mobile: "Mobile", desktop: "Desktop", unknown: "Sconosciuto" }} />
        </div>
      </Section>

      <Section eyebrow="Elenco live" title={`${items.length} sessioni attive`}
        action={<span className="text-[11px] text-[#9A9A9A]">refresh 20s</span>}>
        <Table cols={["Chi", "Pagina", "Dispositivo", "OS", "Browser", "Ultimo ping"]}>
          {items.map((i) => (
            <tr key={i.session_id} className="border-t border-[#F0EBE3]">
              <Td>
                {i.is_guest
                  ? <span className="text-[#7B61FF] font-medium inline-flex items-center gap-1"><Globe className="h-3 w-3" /> Guest</span>
                  : (
                    <span className="inline-flex flex-col">
                      <span className="text-[#1A1A1A]">{i.user_name || "—"}</span>
                      <span className="text-[10px] text-[#9A9A9A]">{i.user_email || ""}</span>
                    </span>
                  )}
              </Td>
              <Td>
                <span className="inline-flex items-center gap-1">
                  {i.page === "landing"
                    ? <><Globe className="h-3 w-3 text-[#7B61FF]" /> Landing</>
                    : i.page?.startsWith("app")
                      ? <><Smartphone className="h-3 w-3 text-[#FF7A1A]" /> {i.page}</>
                      : <span className="text-[#9A9A9A]">{i.page || "—"}</span>}
                </span>
              </Td>
              <Td>
                <span className="inline-flex items-center gap-1">
                  {i.standalone && <span title="PWA installata" className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[#FF7A1A] text-white">PWA</span>}
                  {i.platform === "mobile" ? <Smartphone className="h-3 w-3 text-[#5B9EE0]" /> : <Monitor className="h-3 w-3 text-[#5B9EE0]" />}
                  <span className="text-[#6B6B6B]">{i.platform}</span>
                </span>
              </Td>
              <Td>
                <span className="inline-flex items-center gap-1">
                  {i.os === "iOS" || i.os === "macOS" ? <Apple className="h-3 w-3" /> : null}
                  {i.os}
                </span>
              </Td>
              <Td className="text-[#6B6B6B]">{i.browser}</Td>
              <Td className="text-[#9A9A9A] text-[11px]">{new Date(i.last_ping_at).toLocaleTimeString("it-IT")}</Td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><Td colSpan={6}><div className="text-center py-6 text-[#9A9A9A] text-sm">Nessuno online in questo momento.</div></Td></tr>
          )}
        </Table>
      </Section>
    </>
  );
}

function BreakdownCard({ title, items, color, labelMap, testid }) {
  const entries = Object.entries(items || {}).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  return (
    <div data-testid={testid} className="rounded-2xl border border-[#F0EBE3] bg-white p-4"
      style={{ boxShadow: "0 6px 16px -8px rgba(0,0,0,0.06)" }}>
      <div className="text-[10px] uppercase tracking-widest text-[#9A9A9A] mb-3">{title}</div>
      <div className="space-y-2">
        {entries.length === 0 && <div className="text-[12px] text-[#B0B0B0]">Nessun dato</div>}
        {entries.map(([k, v]) => {
          const pct = total ? (v / total) * 100 : 0;
          return (
            <div key={k}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[12px] text-[#1A1A1A]">{(labelMap && labelMap[k]) || k}</span>
                <span className="text-[11px] font-semibold text-[#1A1A1A]">{v}</span>
              </div>
              <div className="h-1.5 rounded-full bg-[#F4EFE6] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   IllustrationsPanel — admin gallery for /static/illustrations.
   List all PNGs, preview, upload replacement, or regen from prompt.
   ============================================================ */
function IllustrationsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");
  const [v, setV] = useState(() => Date.now()); // cache buster

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/illustrations"); setItems(r.data.items || []); }
    catch (e) { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((i) => !q || i.slug.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <Section eyebrow={`${items.length} immagini · click per gestire`} title="Gestione illustrazioni ORIO"
        action={
          <input
            data-testid="illus-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="cerca slug..."
            className="px-3 py-1.5 rounded-full bg-white border border-[#F0EBE3] text-[12px] text-[#1A1A1A] placeholder-[#B0B0B0] outline-none w-40"
          />
        }
      >
        {loading ? (
          <div className="text-sm text-[#9A9A9A]">Caricamento...</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {filtered.map((it) => (
              <button key={it.slug} onClick={() => setSelected(it)}
                data-testid={`illus-card-${it.slug}`}
                className="group text-left rounded-2xl border border-[#F0EBE3] bg-white overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="aspect-[3/4] bg-[#F5EFE6] overflow-hidden relative">
                  <img src={`${(process.env.REACT_APP_BACKEND_URL || "")}${it.url}?_=${v}`}
                    alt={it.slug} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  {(it.used_in?.length ?? 0) === 0 && (
                    <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-[#FFB020]/95 text-[9px] font-semibold text-white uppercase tracking-wider" title="Nessun uso rilevato">
                      Non usata
                    </span>
                  )}
                </div>
                <div className="p-2.5">
                  <div className="text-[11.5px] font-semibold text-[#1A1A1A] truncate">{it.slug}</div>
                  <div className="text-[10px] text-[#9A9A9A]">{it.size_kb} KB</div>
                  {it.used_in?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {it.used_in.slice(0, 3).map((loc) => (
                        <span key={loc} className="px-1.5 py-0.5 rounded-md text-[9px] font-medium bg-[#F5EFE6] text-[#4A4A4A] border border-[#E5DECF]">
                          {loc}
                        </span>
                      ))}
                      {it.used_in.length > 3 && (
                        <span className="px-1.5 py-0.5 rounded-md text-[9px] font-medium bg-[#F5EFE6] text-[#9A9A9A]">+{it.used_in.length - 3}</span>
                      )}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </Section>

      <AnimatePresence>
        {selected && (
          <IllustrationEditor
            item={selected}
            onClose={() => setSelected(null)}
            onUpdated={() => { setV(Date.now()); load(); }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function IllustrationEditor({ item, onClose, onUpdated }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState(() => Date.now());
  const fileRef = React.useRef(null);

  const doUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/admin/illustrations/${item.slug}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Immagine sostituita");
      setV(Date.now());
      onUpdated?.();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  const doRegen = async () => {
    if (!prompt.trim()) { toast.error("Inserisci un prompt"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/illustrations/${item.slug}/regen`, { prompt });
      toast.success("Immagine rigenerata");
      setV(Date.now());
      onUpdated?.();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[1800] flex items-center justify-center px-4"
      onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <motion.div initial={{ scale: 0.95, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 12 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-3xl bg-white border border-[#F0EBE3] overflow-hidden max-h-[92vh] flex flex-col"
        style={{ boxShadow: "0 24px 60px -20px rgba(0,0,0,0.35)" }}>
        <div className="p-4 border-b border-[#F0EBE3] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#9A9A9A]">Modifica immagine</div>
            <div className="font-display text-lg text-[#1A1A1A]">{item.slug}</div>
            {item.used_in?.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {item.used_in.map((loc) => (
                  <span key={loc} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#F5EFE6] text-[#4A4A4A] border border-[#E5DECF]">
                    {loc}
                  </span>
                ))}
              </div>
            )}
            {(!item.used_in || item.used_in.length === 0) && (
              <div className="mt-1 text-[10px] text-[#B0B0B0]">Non usata attivamente nel codice</div>
            )}
          </div>
          <button data-testid="illus-editor-close" onClick={onClose}
            className="h-9 w-9 rounded-xl bg-[#F5EFE6] hover:bg-[#EDE6D9] flex items-center justify-center">
            <X className="h-4 w-4 text-[#4A4A4A]" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto space-y-4">
          <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-[#F5EFE6] max-h-[46vh] mx-auto">
            <img src={`${(process.env.REACT_APP_BACKEND_URL || "")}${item.url}?_=${v}`}
              alt={item.slug} className="w-full h-full object-contain" />
          </div>

          <div className="rounded-2xl border border-[#F0EBE3] p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#9A9A9A] mb-2">Rigenera con AI (stile ORIO canonico)</div>
            <textarea rows={3}
              data-testid="illus-regen-prompt"
              value={prompt} onChange={(e) => setPrompt(e.target.value)}
              placeholder="Es: donna che cammina con il suo golden retriever in un parco italiano con vista sul Duomo di Firenze..."
              className="w-full px-3 py-2 rounded-xl bg-[#FAF7F0] border border-[#F0EBE3] text-[13px] resize-none outline-none focus:border-[#FF7A1A]" />
            <button data-testid="illus-regen-btn" onClick={doRegen} disabled={busy || !prompt.trim()}
              className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-white font-semibold text-sm disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)" }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <SparklesIcon className="h-4 w-4" />}
              Rigenera con AI
            </button>
          </div>

          <div className="rounded-2xl border border-[#F0EBE3] p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#9A9A9A] mb-2">Carica sostituzione manuale (PNG/JPG/WebP, max 10MB)</div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
              data-testid="illus-upload-input" onChange={doUpload} className="hidden" />
            <button onClick={() => fileRef.current?.click()} disabled={busy}
              data-testid="illus-upload-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#F5EFE6] text-[#1A1A1A] font-semibold text-sm border border-[#F0EBE3]">
              <Upload className="h-4 w-4" /> Carica file
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

